/**
 * Scenarios: historical situations (engine `SCENARIOS`, docs/Scenarios.md) as a stack of case
 * files on the left and the chosen one's briefing on the right — a general-staff order with the
 * lesson, the real-world theatre and its map (docs/Theatres.md), the forces on every side
 * (techs, army strength, reserves, season), the opposition's quality (overrides every AI slot,
 * or "as briefed") and the seed (weather, fortunes of war; the map of a real theatre is fixed).
 * "Take command" starts `scenarioConfig(id, seed, difficulty)` at speed II.
 */
import {
  type AiDifficulty,
  type FactionSetup,
  SCENARIO_IDS,
  SCENARIOS,
  type ScenarioDef,
  type ScenarioId,
  scenarioConfig,
  THEATRES,
} from '@friction/engine';
import { usePersistentState } from '../../app/usePersistentState';
import { type Translator, useT } from '../../i18n';
import { nationName, theatreTitle } from '../../i18n/names';
import { Button, cx, Field, Segmented, TextInput } from '../common/controls';
import { Icon } from '../common/Icon';
import { Paper, Stamp } from '../common/Panel';
import { DIFFICULTY_IDS, ROMAN } from './presets';
import { ScreenFrame } from './ScreenFrame';
import { MapSizeGlyph, useLaunch } from './SinglePlayerSetup';
import sc from './scenarios.module.css';
import s from './screens.module.css';
import { TheatrePreview } from './TheatrePreview';

type Opposition = AiDifficulty | 'briefed';

interface ScenarioSetup {
  id: ScenarioId;
  opposition: Opposition;
  seedMode: 'random' | 'fixed';
  seed: number;
}

const DEFAULTS: ScenarioSetup = { id: 'tannenberg', opposition: 'briefed', seedMode: 'random', seed: 1914 };

/** "×1.4" / "×1,4" — the scenario's army multiplier. */
const factor = (t: Translator, x: number): string => t.num(x, Number.isInteger(x * 10) ? 1 : 2);

/** One side's asymmetries, as a staff officer lists them: quality, strength, techs, reserves. */
function describeSide(t: Translator, f: FactionSetup, opposition: Opposition): string {
  const parts: string[] = [];
  if (f.controller === 'ai') {
    const level = opposition === 'briefed' ? (f.aiDifficulty ?? 'veteran') : opposition;
    parts.push(t(`difficulty.${level}`));
  }
  if (f.army !== undefined && f.army !== 1) parts.push(t('scn.army', { x: factor(t, f.army) }));
  for (const tech of f.techs ?? []) parts.push(t(`tech.${tech}.name`));
  for (const e of f.extraUnits ?? []) parts.push(t('scn.reserve', { n: e.count, unit: t(`unit.${e.type}`) }));
  return parts.length ? parts.join(' · ') : t('scn.nothingSpecial');
}

function forces(t: Translator, def: ScenarioDef, opposition: Opposition): { key: string; val: string }[] {
  const rows = [
    {
      key: t('scn.situation'),
      val: [
        def.theatre
          ? t('scn.theatreReal', {
              name: theatreTitle(THEATRES[def.theatre].name, t.locale),
              season: t(`season.${def.season}`),
            })
          : t('scn.theatre', { size: t(`mapSize.${def.mapSize}`), season: t(`season.${def.season}`) }),
        t('scn.nations', { n: def.factions.length }),
        t('scn.victory', { pct: t.pct(def.vpVictoryShare) }),
        ...(def.dayLimit ? [t('scn.dayLimit', { n: def.dayLimit })] : []),
      ].join(' · '),
    },
  ];
  if (def.startingTechs?.length)
    rows.push({ key: t('scn.everyone'), val: def.startingTechs.map((x) => t(`tech.${x}.name`)).join(' · ') });
  const nations = def.theatre ? THEATRES[def.theatre].nations : null;
  def.factions.forEach((f, i) => {
    const nation = nations?.[i];
    rows.push({
      key: nation
        ? i === 0
          ? `${t('scn.you')} · ${nationName(nation, t.locale)}`
          : nationName(nation, t.locale)
        : i === 0
          ? t('scn.you')
          : t('scn.enemy', { num: ROMAN[i - 1] }),
      val: describeSide(t, f, opposition),
    });
  });
  return rows;
}

export function ScenariosScreen() {
  const t = useT();
  const [cfg, setCfg] = usePersistentState<ScenarioSetup>('friction.scenarios', DEFAULTS);
  const patch = (p: Partial<ScenarioSetup>) => setCfg((c) => ({ ...c, ...p }));
  const { launching, timedOut, launch } = useLaunch();
  const id = SCENARIO_IDS.includes(cfg.id) ? cfg.id : DEFAULTS.id;
  const def = SCENARIOS[id];
  const index = SCENARIO_IDS.indexOf(id);

  const takeCommand = () => {
    const seed = cfg.seedMode === 'random' ? Math.floor(Math.random() * 0x7fffffff) : cfg.seed;
    launch(scenarioConfig(id, seed, cfg.opposition === 'briefed' ? undefined : cfg.opposition), 2);
  };

  return (
    <ScreenFrame
      kicker={t('scn.kicker')}
      title={t('scn.title')}
      footer={<span className={s.note}>{t('scn.footer')}</span>}
    >
      <div className={sc.layout}>
        <div className={sc.files} role="listbox" aria-label={t('scn.title')}>
          {SCENARIO_IDS.map((sid, i) => (
            <button
              key={sid}
              type="button"
              role="option"
              aria-selected={sid === id}
              className={cx(sc.file, sid === id && sc.fileOn)}
              onClick={() => patch({ id: sid })}
            >
              <span className={sc.fileNum}>{ROMAN[i]}.</span>
              <span className={sc.fileBody}>
                <span className={sc.fileTitle}>{t(`scn.${sid}.title`)}</span>
                <span className={sc.fileInspired}>{t(`scn.${sid}.inspired`)}</span>
                <span className={sc.fileLesson}>{t(`scn.${sid}.lesson`)}</span>
              </span>
              <MapSizeGlyph size={SCENARIOS[sid].mapSize} />
            </button>
          ))}
        </div>

        <aside className={sc.side}>
          <Paper className={cx(s.briefing, sc.brief)} key={id}>
            <div className={s.briefHead}>
              <span>{t('sp.brief.head')}</span>
              <span>{t('sp.brief.no', { n: ROMAN[index] })}</span>
            </div>
            <Stamp className={sc.stamp} rotate={-9}>
              {t('scn.stamp')}
            </Stamp>
            <div className={sc.briefTitle}>{t(`scn.${id}.title`)}</div>
            <div className={sc.briefInspired}>{t('scn.inspired', { what: t(`scn.${id}.inspired`) })}</div>
            <p className={sc.briefText}>{t(`scn.${id}.brief`)}</p>
            {def.theatre && (
              <div className={sc.briefMap}>
                <TheatrePreview id={def.theatre} width={300} height={140} label={t('sp.map.preview')} />
              </div>
            )}
            <div className={sc.forcesHead}>{t('scn.forces')}</div>
            <dl className={sc.forces}>
              {forces(t, def, cfg.opposition).map((r) => (
                <div key={r.key} className={sc.force}>
                  <dt>{r.key}</dt>
                  <dd>{r.val}</dd>
                </div>
              ))}
            </dl>
            <div className={s.briefSign}>
              <span>{t('sp.brief.approved')}</span>
              <span className={s.signature}>{t('sp.brief.signature')}</span>
            </div>
          </Paper>

          <div className={sc.controls}>
            <Field label={t('scn.opposition')}>
              <Segmented<Opposition>
                block
                small
                value={cfg.opposition}
                onChange={(v) => patch({ opposition: v })}
                options={[
                  { value: 'briefed', label: t('scn.asBriefed'), title: t('scn.asBriefed.hint') },
                  ...DIFFICULTY_IDS.map((d) => ({
                    value: d,
                    label: t(`difficulty.${d}`),
                    title: t(`difficulty.${d}.hint`),
                  })),
                ]}
              />
            </Field>
            <Field label={t('scn.seed')}>
              <div className={s.row}>
                <Segmented
                  small
                  value={cfg.seedMode}
                  onChange={(v) => patch({ seedMode: v })}
                  options={[
                    { value: 'random', label: t('ui.random') },
                    { value: 'fixed', label: t('ui.fixed') },
                  ]}
                />
                <TextInput
                  mono
                  value={String(cfg.seed)}
                  disabled={cfg.seedMode === 'random'}
                  onChange={(v) =>
                    patch({ seed: Math.max(0, Math.min(0x7fffffff, Number(v.replace(/\D/g, '')) || 0)) })
                  }
                  style={{ width: 110 }}
                  aria-label={t('sp.seed.aria')}
                />
                <Button
                  size="sm"
                  icon="dice"
                  title={t('sp.seed.roll')}
                  disabled={cfg.seedMode === 'random'}
                  onClick={() => patch({ seed: Math.floor(Math.random() * 999_999) })}
                />
              </div>
            </Field>
            <Button variant="primary" size="lg" block icon="flag" disabled={launching} onClick={takeCommand}>
              {launching ? t('sp.launching') : t('scn.take')}
            </Button>
            {timedOut && (
              <div className={s.error}>
                <Icon name="close" />
                <span>{t('sp.timeout')}</span>
              </div>
            )}
          </div>
        </aside>
      </div>
    </ScreenFrame>
  );
}
